import React, { useEffect, useState } from "react";
import "./faqs.scss";
import CommonHeader from "../../components/CommonHeader";
import faqIcon from "../../assets/images/faq-icon.svg";
import useFaqs from "../../Hooks/useFaqs";
import { useAppSelector } from "../../redux/hooks";
import { faqList } from "../../redux/faqs/faqsSlice";
import Loading from "../../components/LoadingPage/Loading";

const FAQ_TYPE_NAME = 'SaaS_1.0';

const Faq = () => {
  const faqListData = useAppSelector(faqList);
  const [searchVal, setSearchVal] = useState<string>("");
  const [faqData, setFaqData] = useState(faqListData?.sections);

  const { getFaqTypesAPI, getFaqListAPI, getFaqListLoading, getFaqTypesLoading } = useFaqs();

  const escapeRegExp = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escapes all special characters
  };

  // Function to highlight search terms in the FAQ text
  const highlightText = (text: string) => {
    if (!searchVal) return text;
    const escapedSearchVal = escapeRegExp(searchVal);
    const regex = new RegExp(`(${escapedSearchVal})`, "gi");
    return text.replace(regex, `<mark>$1</mark>`);
  };

  useEffect(() => {
    if (!searchVal) {
      setFaqData(faqListData?.sections);
    } else {
      const filteredData = faqListData?.sections?.map((section: any) => {
        const filteredFaqs = section?.questionAnss?.filter(
          (qa: any) =>
            qa?.question?.toLowerCase()?.includes(searchVal?.toLowerCase()) ||
            qa?.answer?.toLowerCase()?.includes(searchVal?.toLowerCase())
        );
        return { ...section, questionAnss: filteredFaqs };
      }).filter((section: any) => section?.questionAnss?.length > 0);

      setFaqData(filteredData);
    }
  }, [searchVal, faqListData]);

  useEffect(() => {
    getFaqTypesAPI()
      .then(types => types?.find((item: any) => item.name === FAQ_TYPE_NAME)?.id)
      .then(id => id && getFaqListAPI({ id }))
      .catch(err => console.error('Failed to load FAQs', err));
  }, []);

  const isValidImageUrl = (url: any): boolean => {
    if (!url || typeof url !== 'string') return false;
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
    return imageExtensions.test(url) || url.includes('data:image/');
  }

  return (
    <div className="faqs-wrapper">
      <CommonHeader
        pageTitle="FAQs"
        pageSubtitle={faqListData?.overview || "Search here to get answers to your questions"}
        searchValue={searchVal}
        handleSearchChange={setSearchVal}
      />

      {getFaqTypesLoading || getFaqListLoading ? (
        <Loading />
      ) : (
        <div className="faq-list-wrapper">
          {faqData?.length > 0 ? (
            faqData.map((item: any) => (
              <div className="faq-section" key={item?.id}>
                <div className="faq-section-title">
                  <img
                    src={isValidImageUrl(item?.url) ? item?.url : faqIcon}
                    alt={item?.name}
                    height={32}
                    width={32}
                  />
                  <h4>{item?.name}</h4>
                </div>
                <div className="accordion">
                  {item?.questionAnss?.map((faq: any) => (
                    <div className="accordion-item" key={faq?.id}>
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#que-${item?.id}-${faq.id}`}
                          aria-expanded="false"
                          aria-controls={`que-${item?.id}-${faq.id}`}
                        >
                          <div dangerouslySetInnerHTML={{ __html: highlightText(faq?.question) }}></div>
                        </button>
                      </h2>
                      <div
                        id={`que-${item?.id}-${faq.id}`}
                        className="accordion-collapse collapse"
                      >
                        <div className="accordion-body">
                          <span dangerouslySetInnerHTML={{ __html: highlightText(faq?.answer) }} />
                          {faq?.image && (
                            <img src={faq?.image} alt={faq?.question} width={100} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
              <p className="no-results">No FAQs found {searchVal ? `for "${searchVal}"` : ''}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Faq;
